import {
  RECEIPT_UPLOAD_ENDPOINT,
  CDN_BASE_URL,
} from '../../config/cloudflare';
import type {
  ReceiptUploadResult,
  ReceiptFile,
  NativeReceiptFile,
  WebReceiptFile,
} from './types';

const isNativeFile = (file: ReceiptFile): file is NativeReceiptFile =>
  typeof (file as NativeReceiptFile).uri === 'string';

const isWebFile = (file: ReceiptFile): file is WebReceiptFile =>
  typeof (file as WebReceiptFile).file !== 'undefined' && (file as WebReceiptFile).file !== null;

const ensureTrailingSlash = (value: string): string =>
  value.endsWith('/') ? value : `${value}/`;

const CDN_PREFIX = ensureTrailingSlash(CDN_BASE_URL);

export class ReceiptUploadService {
  private endpoint: string;

  constructor(endpoint: string = RECEIPT_UPLOAD_ENDPOINT) {
    this.endpoint = endpoint;
  }

  async uploadReceipt(
    file: ReceiptFile,
    userId: string,
    groupId?: string
  ): Promise<ReceiptUploadResult> {
    try {
      if (!isNativeFile(file) && !isWebFile(file)) {
        throw new Error('Unsupported receipt file format.');
      }

      const formData = new FormData();

      if (isNativeFile(file)) {
        formData.append(
          'file',
          {
            uri: file.uri,
            type: file.type,
            name: file.name,
          } as any
        );
      } else if (isWebFile(file)) {
        const actualFile: any = (file as WebReceiptFile).file;
        const fallbackName = file.name || actualFile?.name || `receipt-${Date.now()}.jpg`;
        const fallbackType = file.type || actualFile?.type || 'image/jpeg';
        formData.append('file', actualFile, fallbackName);

        // Some environments require explicit type override
        if (typeof actualFile === 'object' && actualFile && !actualFile.type && fallbackType) {
          try {
            Object.defineProperty(actualFile, 'type', {
              value: fallbackType,
              configurable: true,
            });
          } catch {
            // ignore when type is read-only
          }
        }
      }

      formData.append('userId', userId);
      if (groupId) {
        formData.append('groupId', groupId);
      }

      const response = await fetch(this.endpoint, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        const errorMessage =
          result?.error || `HTTP ${response.status}: ${response.statusText}`;
        return {
          success: false,
          error: errorMessage,
        };
      }

      return {
        success: true,
        data: result.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error?.message || '网络错误，请重试',
      };
    }
  }

  async deleteReceipt(key: string, userId: string): Promise<boolean> {
    try {
      const deleteUrl = `${this.endpoint}?key=${encodeURIComponent(
        key
      )}&userId=${encodeURIComponent(userId)}`;

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
      });

      const result = await response.json();
      return Boolean(response.ok && result?.success);
    } catch {
      return false;
    }
  }

  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(this.endpoint, {
        method: 'OPTIONS',
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  extractKeyFromUrl(url: string): string | null {
    if (typeof url !== 'string') {
      return null;
    }

    if (url.startsWith(CDN_PREFIX)) {
      return url.substring(CDN_PREFIX.length);
    }

    return null;
  }
}

export const receiptUploadService = new ReceiptUploadService();

export default ReceiptUploadService;
